import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Landmark } from "lucide-react";
import { Page, PageHero, Eyebrow } from "@/components/PageShell";
import { COMPANY, IMAGES } from "@/data/site";

export default function Contact() {
  return (
    <Page testid="contact-page">
      <PageHero
        eyebrow="Geneva Headquarters"
        title="At the water's edge of Swiss finance."
        subtitle="Our office sits on Quai de l'Île, in the historic heart of Geneva — minutes from the Mirabaud Group's headquarters on the Rhône."
        image={IMAGES.genevaLake}
        testid="contact-hero"
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-16" data-testid="contact-content">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Eyebrow>Reach Us</Eyebrow>
          <h2 className="mt-5 font-serif-display text-2xl sm:text-3xl lg:text-4xl font-normal tracking-tight text-[#F8F9FA]">
            Correspondence & visits
          </h2>
          <p className="mt-5 text-base leading-relaxed text-[#A0AAB2] max-w-lg">
            For enquiries regarding Mirabaud investment marketing mandates, please
            contact the Geneva office directly by telephone or written correspondence.
          </p>

          <div className="mt-10 space-y-5">
            <div
              className="border border-[rgba(197,160,89,0.25)] bg-[#162248] p-7 flex items-start gap-5"
              data-testid="contact-address-block"
            >
              <div className="w-11 h-11 border border-[rgba(197,160,89,0.4)] flex items-center justify-center shrink-0">
                <MapPin size={18} className="text-[#C5A059]" />
              </div>
              <div>
                <span className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#6C7A89]">
                  Office Address
                </span>
                <p className="mt-2 text-base text-[#F8F9FA] leading-relaxed">
                  Lac Léman
                  <br />
                  {COMPANY.address}
                </p>
              </div>
            </div>

            <a
              href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
              className="border border-[rgba(197,160,89,0.25)] bg-[#162248] p-7 flex items-start gap-5 card-hover block"
              data-testid="contact-phone-link"
            >
              <div className="w-11 h-11 border border-[rgba(197,160,89,0.4)] flex items-center justify-center shrink-0">
                <Phone size={18} className="text-[#C5A059]" />
              </div>
              <div>
                <span className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#6C7A89]">
                  Telephone
                </span>
                <p className="mt-2 text-base text-[#F8F9FA]">{COMPANY.phone}</p>
              </div>
            </a>

            <a
              href={`mailto:${COMPANY.email}`}
              className="border border-[rgba(197,160,89,0.25)] bg-[#162248] p-7 flex items-start gap-5 card-hover block"
              data-testid="contact-email-link"
            >
              <div className="w-11 h-11 border border-[rgba(197,160,89,0.4)] flex items-center justify-center shrink-0">
                <Mail size={18} className="text-[#C5A059]" />
              </div>
              <div>
                <span className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#6C7A89]">
                  Electronic Mail
                </span>
                <p className="mt-2 text-base text-[#F8F9FA]">{COMPANY.email}</p>
              </div>
            </a>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-6 text-sm text-[#6C7A89]">
            <span className="flex items-center gap-2.5">
              <Clock size={15} className="text-[#C5A059]" />
              Monday – Friday, 08:30 – 18:00 CET
            </span>
            <span className="flex items-center gap-2.5">
              <Landmark size={15} className="text-[#C5A059]" />
              Visits by appointment only
            </span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="space-y-8"
        >
          <div className="relative" data-testid="contact-map-frame">
            <div className="absolute -top-4 -left-4 w-full h-full border border-[rgba(197,160,89,0.3)]" />
            <iframe
              title="Lac Léman office location — Quai de l'Île 13, Geneva"
              src="https://www.openstreetmap.org/export/embed.html?bbox=6.1340%2C46.1990%2C6.1560%2C46.2120&layer=mapnik&marker=46.2055%2C6.1450"
              className="relative w-full h-[380px] border-0 grayscale invert-[0.92] hue-rotate-180 contrast-[0.9]"
              loading="lazy"
            />
          </div>
          <div className="border border-[rgba(197,160,89,0.25)] bg-[#162248] p-8" data-testid="contact-parent-note">
            <Eyebrow>A Note on Correspondence</Eyebrow>
            <p className="mt-4 text-sm leading-relaxed text-[#A0AAB2]">
              Lac Léman operates exclusively as the marketing branch of the Mirabaud
              Group. Investment enquiries are referred to the Group's relationship
              managers in Geneva. All communications are treated with Swiss banking
              discretion.
            </p>
          </div>
        </motion.div>
      </section>
    </Page>
  );
}
